/**
 * ContactEditPanel - where a contact's details are CHANGED (con-3).
 *
 * They used to be changed on the canvas: the Details tab's one "Contact
 * Information" card flipped into a form in place, so the facts you were
 * editing against disappeared the moment you started editing them, and the
 * canvas had two completely different shapes depending on a boolean. Every
 * other record in the platform edits through a side panel - matter pricing,
 * project pricing, time entries, access - so a contact does too: the canvas
 * stays readable behind it, and the form is one column with its own footer.
 *
 * WHAT IT WRITES. `PUT /clients/<uuid>` with only the keys that changed. The
 * old inline form sent all nine fields every save with `||` fallbacks, which
 * meant clearing a phone number wrote the old phone number back. A diff of the
 * draft against the loaded contact fixes that: an emptied field is sent as an
 * empty string, and an untouched field is not sent at all.
 *
 * `is_individual` is derived from the chosen type, exactly as the inline form
 * derived it, so the Contact Person block appears for a company and not for a
 * person.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Spinner from '../shared/TailwindComponents/Spinner';

/**
 * The type ids the create form treats as a natural person. Lifted verbatim
 * from the inline edit form it replaces - the dropdown list carries no
 * `is_individual` flag, so this is still the only way to derive it client-side.
 */
const INDIVIDUAL_TYPE_IDS = new Set([6, 54, 55, 41, 25, 73, 19, 63, 42]);

/** Every field the panel can change. Strings throughout - controls need them. */
export interface ContactDraft {
  name: string;
  type_id: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  contact_person_name: string;
  contact_person_email: string;
  notes: string;
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

export function draftFromContact(client: any): ContactDraft {
  return {
    name: str(client?.name),
    type_id: str(client?.type_id),
    contact_email: str(client?.contact_email),
    contact_phone: str(client?.contact_phone),
    address: str(client?.address),
    contact_person_name: str(client?.contact_person_name),
    contact_person_email: str(client?.contact_person_email),
    notes: str(client?.notes),
  };
}

/**
 * Only what changed. Text is trimmed before comparison, so adding and removing
 * a space is not an edit; an emptied field IS an edit and is sent as ''.
 */
export function diffContactDraft(draft: ContactDraft, client: any): Record<string, any> {
  const original = draftFromContact(client);
  const payload: Record<string, any> = {};

  (Object.keys(draft) as (keyof ContactDraft)[]).forEach((key) => {
    const next = draft[key].trim();
    if (next === original[key].trim()) return;
    if (key === 'type_id') {
      const typeId = parseInt(next, 10);
      if (!Number.isNaN(typeId)) {
        payload.type_id = typeId;
        payload.is_individual = INDIVIDUAL_TYPE_IDS.has(typeId);
      }
      return;
    }
    payload[key] = next;
  });

  return payload;
}

/** Validation the panel blocks Save on. Keys match `ContactDraft`. */
export function validateContactDraft(
  draft: ContactDraft,
  t: (key: string) => string,
): Partial<Record<keyof ContactDraft, string>> {
  const errors: Partial<Record<keyof ContactDraft, string>> = {};
  if (!draft.name.trim()) {
    errors.name = t('Name is required');
  } else if (draft.name.trim().length > 150) {
    errors.name = t('Name must be less than 150 characters');
  }
  if (!draft.type_id.trim()) errors.type_id = t('Type is required');

  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (draft.contact_email.trim() && !email.test(draft.contact_email.trim())) {
    errors.contact_email = t('Enter a valid email address');
  }
  if (draft.contact_person_email.trim() && !email.test(draft.contact_person_email.trim())) {
    errors.contact_person_email = t('Enter a valid email address');
  }
  return errors;
}

export interface ContactEditPanelProps {
  client: any;
  /** The `/dropdown/client-types` list, already loaded by the page. */
  contactTypes: any[];
  /** Receives the saved contact so the page re-renders without a refetch. */
  onSaved?: (client: any) => void;
  onClose: () => void;
}

const ContactEditPanel: React.FC<ContactEditPanelProps> = ({
  client,
  contactTypes,
  onSaved,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const initial = useMemo(() => draftFromContact(client), [client]);
  const [draft, setDraft] = useState<ContactDraft>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactDraft, string>>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const setField = (key: keyof ContactDraft, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const changes = useMemo(() => diffContactDraft(draft, client), [draft, client]);
  const isDirty = Object.keys(changes).length > 0;

  /* Company or person, from the DRAFT's type - so switching the type in the
     dropdown reveals or hides the Contact Person block immediately, without a
     save. */
  const isCompany = useMemo(() => {
    const typeId = parseInt(draft.type_id, 10);
    if (!Number.isNaN(typeId)) return !INDIVIDUAL_TYPE_IDS.has(typeId);
    return client?.is_individual === false;
  }, [draft.type_id, client]);

  const shownErrors = showErrors ? errors : {};

  const handleSave = async () => {
    const found = validateContactDraft(draft, t);
    setErrors(found);
    setShowErrors(true);
    if (Object.keys(found).length > 0) return;
    if (!isDirty) return;

    try {
      setIsSaving(true);
      const resp = await apis({
        method: 'PUT',
        url: `/clients/${client.uuid}`,
        data: changes,
      });

      if (resp.status === 200 && resp.data?.success) {
        // The endpoint echoes the saved record; fall back to the local merge
        // so the page still updates on a backend that returns only `success`.
        const saved = resp.data.client || { ...client, ...changes };
        onSaved?.(saved);
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'success',
          message: t('Contact updated successfully'),
          duration: 3000,
        }));
        onClose();
      } else {
        throw new Error('save failed');
      }
    } catch {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: t('Failed to update contact'),
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-5 py-5 space-y-5">
        <Input
          id="contact-name"
          type="text"
          label={isCompany ? t('Organization Name') : t('Full Name')}
          value={draft.name}
          onChange={(e) => setField('name', e.target.value)}
          error={shownErrors.name}
          disabled={isSaving}
          required
          className="w-full"
        />

        <div>
          <label className="block text-sm font-medium text-fg-body mb-1" htmlFor="contact-type">
            {t('Contact Type')}
          </label>
          <div className={isSaving ? 'pointer-events-none opacity-60' : undefined}>
            <Dropdown
              id="contact-type"
              options={contactTypes}
              value={draft.type_id}
              hierarchical
              onSelect={(option: any) => setField('type_id', String(option.value))}
              placeholder={t('Select type')}
              className="w-full"
            />
          </div>
          {shownErrors.type_id && (
            <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{shownErrors.type_id}</p>
          )}
        </div>

        <Input
          id="contact-email"
          type="email"
          label={isCompany ? t('Organization Email') : t('Email')}
          value={draft.contact_email}
          onChange={(e) => setField('contact_email', e.target.value)}
          error={shownErrors.contact_email}
          disabled={isSaving}
          className="w-full"
        />

        <Input
          id="contact-phone"
          type="tel"
          label={t('Phone')}
          value={draft.contact_phone}
          onChange={(e) => setField('contact_phone', e.target.value)}
          disabled={isSaving}
          className="w-full"
        />

        <Input
          id="contact-address"
          type="text"
          label={t('Address')}
          value={draft.address}
          onChange={(e) => setField('address', e.target.value)}
          disabled={isSaving}
          className="w-full"
        />

        {/* An organization is reached THROUGH a person; a person is reached
            directly. So this block belongs to companies only - same rule the
            inline form had, kept. */}
        {isCompany && (
          <div className="space-y-5 pt-4 border-t border-hairline-soft">
            <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
              {t('Contact Person')}
            </p>
            <Input
              id="contact-person-name"
              type="text"
              label={t('Contact Person Name')}
              value={draft.contact_person_name}
              onChange={(e) => setField('contact_person_name', e.target.value)}
              disabled={isSaving}
              className="w-full"
            />
            <Input
              id="contact-person-email"
              type="email"
              label={t('Contact Person Email')}
              value={draft.contact_person_email}
              onChange={(e) => setField('contact_person_email', e.target.value)}
              error={shownErrors.contact_person_email}
              disabled={isSaving}
              className="w-full"
            />
          </div>
        )}

        <Textarea
          id="contact-notes"
          label={t('Notes')}
          value={draft.notes}
          onChange={(e) => setField('notes', e.target.value)}
          placeholder={t('Add any additional information about this contact')}
          disabled={isSaving}
          rows={5}
          expandable
          className="w-full"
        />
      </div>

      <div className="flex-shrink-0 flex items-center justify-between gap-3 border-t border-hairline-soft px-5 py-4">
        <span className="text-[11px] text-fg-muted">
          {isDirty ? (
            <span className="inline-flex items-center gap-1.5 text-champagne">
              <span className="h-1.5 w-1.5 rounded-full bg-champagne" />
              {t('Unsaved changes')}
            </span>
          ) : null}
        </span>
        <div className="flex items-center gap-3">
          <Button variant="neutral" size="small" onClick={onClose} disabled={isSaving}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={isSaving || !isDirty}
            tooltip={!isDirty && !isSaving ? t('No changes to save') : ''}
            icon={isSaving ? <Spinner size="glyph" tone="current" /> : undefined}
          >
            {isSaving ? t('Saving...') : t('Save changes')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactEditPanel;
